import { ETAPES, ETAPE_INFO, type Etape } from './etapes.js';
import { TAILLE_CIBLE, cibleDe, foyersPresents, nombreHotesNecessaire, nombreParticipants, plageDe, previsionTables, taillePresente, volontaires as volontairesDe, type Cible } from './foyers.js';
import { calculerScore } from './score.js';
import type { AffectationHote, Alerte, Foyer, Groupe } from './types.js';

/** Alertes d'hôtes qui empêchent de lancer la répartition. */
export const CODES_BLOQUANTS_HOTES = ['etape_sans_hote', 'hote_absent', 'hote_non_volontaire', 'hote_multi_etapes'] as const;
export const estBloquanteHotes = (a: Alerte): boolean => (CODES_BLOQUANTS_HOTES as readonly string[]).includes(a.code);

/** Plus de deux étapes au total pour une voiture : jamais, même forcé. */
export const ETAPES_HOTE_MAX = 2;

/** Les deux étapes ne se suivent pas (entrée + dessert) : la voiture doit partir au plat puis rentrer recevoir. */
export function etapesNonConsecutives(etapes: Etape[]): boolean {
  const idx = etapes.map((e) => ETAPES.indexOf(e)).sort((a, b) => a - b);
  return idx.length === 2 && idx[1]! - idx[0]! > 1;
}

/**
 * Règles 2, 4, 5 : vérifications sur la liste des foyers et le choix des hôtes,
 * avant toute répartition. Les alertes informent sans bloquer, sauf `etape_sans_hote`
 * et `hote_absent` qui empêchent de lancer le moteur.
 */
export function verifierHotes(foyers: Foyer[], hotes: AffectationHote[], cible: Cible = TAILLE_CIBLE): Alerte[] {
  const alertes: Alerte[] = [];
  const plage = plageDe(cible);
  const parId = new Map(foyers.map((f) => [f.id, f]));
  const presents = foyersPresents(foyers);
  const participants = nombreParticipants(foyers);

  // Règle 4 : une voiture hôte reçoit à une seule étape, sauf exception forcée par l'organisateur (2 étapes au plus).
  const vus = new Set<string>();
  const etapesDe = new Map<string, AffectationHote[]>();
  for (const h of hotes) {
    vus.add(h.foyerId);
    etapesDe.set(h.foyerId, [...(etapesDe.get(h.foyerId) ?? []), h]);
  }
  for (const [foyerId, hs] of etapesDe) {
    if (hs.length < 2) continue;
    const nom = nomFoyer(parId.get(foyerId));
    const etapes = hs.map((h) => h.etape);
    const doublon = new Set(etapes).size !== etapes.length;
    if (doublon || hs.length > ETAPES_HOTE_MAX) {
      alertes.push({ code: 'hote_multi_etapes', niveau: 'attention', foyerId, message: `${nom} est placée sur ${hs.length} réceptions : ${ETAPES_HOTE_MAX} étapes différentes au plus.` });
    } else if (!hs.every((h) => h.accordDeuxEtapes)) {
      alertes.push({ code: 'hote_multi_etapes', niveau: 'attention', foyerId, message: `${nom} est placée sur plusieurs étapes : une voiture ne reçoit qu'une seule fois, sauf si vous forcez deux étapes avec son accord.` });
    } else {
      const libelles = ETAPES.filter((e) => etapes.includes(e)).map((e) => ETAPE_INFO[e].label.toLowerCase());
      alertes.push({
        code: 'hote_deux_etapes',
        niveau: 'attention',
        foyerId,
        message: `${nom} reçoit à deux étapes (${libelles.join(' et ')}), à votre demande.${etapesNonConsecutives(etapes) ? ' Ces étapes ne se suivent pas : la voiture part au plat puis rentre recevoir le dessert.' : ''}`,
      });
    }
  }

  for (const h of hotes) {
    const f = parId.get(h.foyerId);
    if (!f) continue;
    // Règle 2 : seuls les présents comptent.
    if (taillePresente(f) === 0) {
      alertes.push({ code: 'hote_absent', niveau: 'attention', etape: h.etape, foyerId: f.id, message: `${nomFoyer(f)} ne participe plus et ne peut pas recevoir ${ETAPE_INFO[h.etape].au}.` });
      continue;
    }
    // Un foyer qui ne souhaite plus recevoir ne peut rester hôte qu'avec un accord explicite.
    if (!f.veutRecevoir && !h.accordHorsDispo) {
      alertes.push({ code: 'hote_non_volontaire', niveau: 'attention', etape: h.etape, foyerId: f.id, message: `${nomFoyer(f)} ne souhaite plus recevoir : retirez-${f.membres.length > 1 ? 'les' : 'le'} des hôtes ${ETAPE_INFO[h.etape].au}, ou confirmez son accord.` });
      continue;
    }
    // Règle 4 : disponibilités = liste de possibilités ; hors choix signalé jusqu'à accord.
    if (!f.disponibilites.includes(h.etape) && !h.accordHorsDispo) {
      const choix = f.disponibilites.map((e) => ETAPE_INFO[e].label.toLowerCase()).join(', ') || 'aucune étape';
      alertes.push({ code: 'hote_hors_dispo', niveau: 'attention', etape: h.etape, foyerId: f.id, message: `${nomFoyer(f)} a indiqué : ${choix} uniquement. La carte restera signalée jusqu'à leur accord.` });
    }
  }

  // Règle 5 : chaque étape a ≥ 1 hôte ; nombre idéal ≈ participants ÷ 4.
  const ideal = nombreHotesNecessaire(participants, cible, volontairesDe(foyers));
  for (const etape of ETAPES) {
    const hotesEtape = hotes
      .filter((h) => h.etape === etape)
      .map((h) => parId.get(h.foyerId))
      .filter((f): f is Foyer => !!f && taillePresente(f) > 0);
    const n = hotesEtape.length;
    if (n === 0) {
      alertes.push({ code: 'etape_sans_hote', niveau: 'attention', etape, message: `Personne ne reçoit ${ETAPE_INFO[etape].au} : il faut au moins 1 hôte.` });
      continue;
    }
    // Capacités déclarées : la somme des places doit couvrir tous les participants.
    const places = capaciteEtape(hotesEtape);
    if (places !== null && places < participants) {
      alertes.push({ code: 'capacite_insuffisante', niveau: 'attention', etape, message: `${ETAPE_INFO[etape].label} : ${places} places chez les hôtes pour ${participants} participants, il en manque ${participants - places}. Ajoutez un hôte ou revoyez les capacités.` });
    }
    const prev = previsionTables(participants, hotesEtape);
    if (participants > 0 && prev.manque === 0 && (prev.min < plage.min || prev.max > plage.max)) {
      const tailles = prev.min === prev.max ? `des groupes de ${prev.max}` : `des groupes de ${prev.min} à ${prev.max}`;
      alertes.push({ code: 'nombre_hotes', niveau: 'attention', etape, message: `${ETAPE_INFO[etape].label} : ${n} hôte${n > 1 ? 's' : ''} pour ${participants} participants, soit ${tailles} (plage conseillée ${plage.min} à ${plage.max}, idéal ${ideal} hôtes).` });
    }
  }

  // Volontaires non utilisés (information, avec action « Ajouter »).
  for (const f of presents) {
    if (f.veutRecevoir && !vus.has(f.id)) {
      alertes.push({ code: 'volontaire_non_utilise', niveau: 'info', foyerId: f.id, message: `${nomFoyer(f)}, volontaire, n'est pas utilisé.` });
    }
  }

  if (!alertes.some(estBloquanteHotes)) {
    alertes.push({ code: 'pret', niveau: 'ok', message: 'Vous pouvez lancer la répartition : 3 étapes couvertes.' });
  }
  return alertes;
}

/** Vrai si les règles bloquantes (étape sans hôte, hôte absent) sont respectées. */
export function peutRepartir(foyers: Foyer[], hotes: AffectationHote[], cible: Cible = TAILLE_CIBLE): boolean {
  return !verifierHotes(foyers, hotes, cible).some(estBloquanteHotes);
}

/**
 * Règles 1, 3, 6, 7, 8 : vérifications sur une répartition (générée ou modifiée à la main).
 */
export function verifierRepartition(foyers: Foyer[], groupes: Groupe[], cible: Cible = TAILLE_CIBLE): Alerte[] {
  const alertes: Alerte[] = [];
  const plage = plageDe(cible);
  const parId = new Map(foyers.map((f) => [f.id, f]));
  const presents = foyersPresents(foyers);

  for (const etape of ETAPES) {
    const ge = groupes.filter((g) => g.etape === etape);
    const occurrences = new Map<string, number>();
    for (const g of ge) for (const id of g.foyerIds) occurrences.set(id, (occurrences.get(id) ?? 0) + 1);
    // Règle 1 : tous les participants prennent part aux 3 étapes.
    for (const f of presents) {
      const n = occurrences.get(f.id) ?? 0;
      if (n === 0) alertes.push({ code: 'participant_manquant', niveau: 'attention', etape, foyerId: f.id, message: `${nomFoyer(f)} n'a pas de table ${ETAPE_INFO[etape].au}.` });
      // Règle 3 : un foyer est un bloc indivisible, donc dans un seul groupe par étape.
      if (n > 1) alertes.push({ code: 'foyer_separe', niveau: 'attention', etape, foyerId: f.id, message: `${nomFoyer(f)} apparaît dans ${n} groupes ${ETAPE_INFO[etape].au}.` });
    }
    // Règle 6 : écart max d'1 personne entre groupes, l'hôte compte dans son groupe —
    // sauf quand une capacité déclarée pleine empêche d'équilibrer davantage.
    const tailles = ge.map((g) => g.foyerIds.reduce((s, id) => s + taillePresente(parId.get(id) ?? ({ membres: [] } as unknown as Foyer)), 0));
    const unePleine = ge.some((g, i) => {
      const cap = parId.get(g.hoteFoyerId)?.capaciteMax;
      return cap !== undefined && (tailles[i] ?? 0) >= cap;
    });
    if (tailles.length > 1 && Math.max(...tailles) - Math.min(...tailles) > 1 && !unePleine) {
      alertes.push({ code: 'groupes_desequilibres', niveau: 'attention', etape, message: `${ETAPE_INFO[etape].label} : groupes de ${Math.min(...tailles)} à ${Math.max(...tailles)} personnes, l'écart dépasse 1.` });
    }
  }

  const score = calculerScore(foyers, groupes, cible);
  // Règle 7 : taille cible 4 ; hors de [3, 5] → avertissement sans blocage.
  for (const g of score.groupes) {
    if (g.horsPlage) {
      alertes.push({ code: 'groupe_hors_plage', niveau: 'attention', etape: g.etape, foyerId: g.hoteFoyerId, message: `Chez ${nomFoyer(parId.get(g.hoteFoyerId))} ${ETAPE_INFO[g.etape].au} : ${g.taille} personne${g.taille > 1 ? 's' : ''}, hors de la plage ${plage.min} à ${plage.max} (cible ${cibleDe(cible)}).` });
    }
  }
  // Capacité déclarée par l'hôte : jamais dépassée.
  for (const g of score.groupes) {
    if (g.capaciteDepassee) {
      alertes.push({ code: 'capacite_depassee', niveau: 'attention', etape: g.etape, foyerId: g.hoteFoyerId, message: `Chez ${nomFoyer(parId.get(g.hoteFoyerId))} ${ETAPE_INFO[g.etape].au} : ${g.taille} personnes pour ${g.capaciteMax} places annoncées.` });
    }
  }
  // Règle 8 : jamais la même table à plus d'une étape ; sinon minimiser et afficher les paires.
  if (score.pairesEnDouble.length > 0) {
    const n = score.pairesEnDouble.length;
    alertes.push({ code: 'rencontres_en_double', niveau: 'info', message: `${n} paire${n > 1 ? 's' : ''} de personnes ensemble à deux étapes. Les paires concernées sont surlignées sur les cartes.` });
  }
  return alertes;
}

/** Somme des capacités des hôtes d'une étape ; null si au moins un hôte n'a pas de limite. */
export function capaciteEtape(hotes: Foyer[]): number | null {
  let total = 0;
  for (const f of hotes) {
    if (f.capaciteMax === undefined) return null;
    total += f.capaciteMax;
  }
  return total;
}

function nomFoyer(f: Foyer | undefined): string {
  return f?.nom ?? 'Une voiture';
}

export function etapesHorsPlage(groupes: Groupe[], foyers: Foyer[]): Etape[] {
  return [...new Set(calculerScore(foyers, groupes).groupes.filter((g) => g.horsPlage).map((g) => g.etape))];
}
